(() => {
  if (customElements.get('freshka-product-looks')) return;

  class FreshkaProductLooks extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.track = this.querySelector('[data-gallery-track]');
      this.links = Array.from(this.querySelectorAll('[data-gallery-image]'));
      this.dialog = this.querySelector('[data-gallery-dialog]');
      if (!this.track || !this.links.length) return;
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.rtl = getComputedStyle(this).direction === 'rtl';
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.previous = this.querySelector('[data-gallery-previous]');
      this.next = this.querySelector('[data-gallery-next]');
      this.navigation = this.querySelector('[data-gallery-navigation]');
      this.status = this.querySelector('[data-gallery-status]');
      this.dialogMedia = this.querySelector('[data-dialog-media]');
      this.dialogPrevious = this.querySelector('[data-dialog-previous]');
      this.dialogNext = this.querySelector('[data-dialog-next]');
      this.dialogPosition = this.querySelector('[data-dialog-position]');
      this.previous.addEventListener('click', () => this.scrollToIndex(this.currentIndex() - 1), options);
      this.next.addEventListener('click', () => this.scrollToIndex(this.currentIndex() + 1), options);
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), { ...options, passive: true });
      this.track.addEventListener('keydown', (event) => this.onTrackKeydown(event), options);
      if (typeof this.dialog?.showModal === 'function') {
        this.links.forEach((link, index) => {
          link.setAttribute('aria-haspopup', 'dialog');
          link.addEventListener('click', (event) => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            this.openDialog(index, link);
          }, options);
        });
        this.querySelector('[data-dialog-close]').addEventListener('click', () => this.dialog.close(), options);
        this.dialogPrevious.addEventListener('click', () => this.showImage(this.activeIndex - 1), options);
        this.dialogNext.addEventListener('click', () => this.showImage(this.activeIndex + 1), options);
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close();
        }, options);
        this.dialog.addEventListener('keydown', (event) => this.onDialogKeydown(event), options);
        this.dialog.addEventListener('close', () => this.onDialogClose(), options);
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', () => this.scheduleUpdate(), options);
      }
      this.updateNavigation();
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.onDialogClose(false);
      this.abortController?.abort();
      this.abortController = null;
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.updateFrame);
    }

    currentIndex() {
      const track = this.track.getBoundingClientRect();
      let nearest = 0;
      let distance = Infinity;
      this.links.forEach((link, index) => {
        const rect = link.getBoundingClientRect();
        const delta = Math.abs(this.rtl ? track.right - rect.right : rect.left - track.left);
        if (delta < distance) { nearest = index; distance = delta; }
      });
      return nearest;
    }

    positionLabel(index) {
      return this.dataset.positionLabel.replace('[current]', String(index + 1));
    }

    scheduleUpdate() {
      cancelAnimationFrame(this.updateFrame);
      this.updateFrame = requestAnimationFrame(() => this.updateNavigation());
    }

    updateNavigation() {
      const track = this.track.getBoundingClientRect();
      const first = this.links[0].getBoundingClientRect();
      const last = this.links[this.links.length - 1].getBoundingClientRect();
      const overflowing = this.track.scrollWidth > this.track.clientWidth + 2;
      this.navigation.hidden = !overflowing;
      this.previous.disabled = !overflowing || (this.rtl ? first.right <= track.right + 2 : first.left >= track.left - 2);
      this.next.disabled = !overflowing || (this.rtl ? last.left >= track.left - 2 : last.right <= track.right + 2);
      const label = this.positionLabel(this.currentIndex());
      if (this.status.textContent !== label) this.status.textContent = label;
    }

    scrollToIndex(index, focus = false) {
      const link = this.links[Math.max(0, Math.min(this.links.length - 1, index))];
      if (focus) link.focus({ preventScroll: true });
      link.scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    }

    onTrackKeydown(event) {
      if (event.altKey || event.metaKey || event.ctrlKey) return;
      const focused = this.links.indexOf(event.target.closest('[data-gallery-image]'));
      const index = focused >= 0 ? focused : this.currentIndex();
      let target;
      if (event.key === 'ArrowRight') target = index + (this.rtl ? -1 : 1);
      if (event.key === 'ArrowLeft') target = index + (this.rtl ? 1 : -1);
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = this.links.length - 1;
      if (target === undefined) return;
      event.preventDefault();
      this.scrollToIndex(target, true);
    }

    openDialog(index, trigger) {
      this.returnFocus = trigger;
      this.showImage(index);
      this.dialog.showModal();
      document.dispatchEvent(new CustomEvent('freshka:media-open', { detail: { source: this } }));
    }

    showImage(index) {
      this.activeIndex = Math.max(0, Math.min(this.links.length - 1, index));
      const link = this.links[this.activeIndex];
      const image = new Image();
      image.alt = link.dataset.imageAlt;
      image.width = Number(link.dataset.imageWidth);
      image.height = Number(link.dataset.imageHeight);
      image.decoding = 'async';
      image.sizes = '(min-width: 1140px) 1100px, calc(100vw - 40px)';
      image.srcset = link.dataset.imageSrcset;
      image.src = link.href;
      this.dialogMedia.replaceChildren(image);
      this.dialogPosition.textContent = this.positionLabel(this.activeIndex);
      this.dialogPrevious.disabled = this.activeIndex === 0;
      this.dialogNext.disabled = this.activeIndex === this.links.length - 1;
    }

    onDialogKeydown(event) {
      let target;
      if (event.key === 'ArrowRight') target = this.activeIndex + (this.rtl ? -1 : 1);
      if (event.key === 'ArrowLeft') target = this.activeIndex + (this.rtl ? 1 : -1);
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = this.links.length - 1;
      if (target === undefined) return;
      event.preventDefault();
      this.showImage(target);
    }

    onDialogClose(restoreFocus = true) {
      this.dialogMedia?.replaceChildren();
      if (restoreFocus && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }
  }

  customElements.define('freshka-product-looks', FreshkaProductLooks);
})();
